import { networkPoller } from "~/features/network-connectivity/NetworkPoller";

import { datadogRum } from "@datadog/browser-rum";

/**
 * A centralized helper for making HTTP requests that return JSON.
 * Used by TanStack Query for client-side data fetching.
 *
 * This function wraps the native fetch API to provide:
 * - Consistent error handling: it throws for non-OK HTTP responses.
 * - Timeout support: requests are aborted if they exceed the specified timeout.
 * - Automatic JSON parsing.
 * - Integration with Datadog RUM for automatic error tracking.
 *
 * Using this helper helps avoid boilerplate and ensures all
 * network requests follow the same error handling, timeout,
 * and logging patterns, making the codebase easier to maintain
 * and monitor.
 *
 * @param path - The resource URL to fetch.
 * @param init - Optional fetch initialization options.
 * @param timeoutMs - Timeout in milliseconds (0 = no timeout).
 * @returns A promise that resolves with the parsed JSON response.
 * @throws Will throw an error if the response is not OK or if the request times out.
 */
export const fetchJson = async <TResponse>(
  path: string,
  options: {
    init?: RequestInit;
    timeoutMs?: number;
    params?: URLSearchParams;
  } = {},
): Promise<TResponse> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    // Default timeout 28 seconds if not specified since api-gateway has a 29s timeout
    options.timeoutMs || 28_000,
  );

  const url = new URL(path, window.origin);
  if (options.params) {
    for (const [key, value] of options.params) {
      url.searchParams.append(key, value);
    }
  }

  try {
    const res = await fetch(url, {
      ...options.init,
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text();
      console.error({ ...res, text });
      throw new Error(`${res.status} ${res.statusText}`);
    }
    return res.json();
  } catch (error) {
    datadogRum.addError(error, { url, functionName: "fetchJson" });
    // Polling to ensure we update online status because some android devices don't trigger online/offline events correctly.
    await networkPoller.startPolling();
    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const postJson = async <TResponse, Body = unknown>(
  url: string,
  body: Body,
  options: {
    init?: RequestInit;
    timeoutMs?: number;
    params?: URLSearchParams;
  } = {},
): Promise<TResponse> => {
  const controller = new AbortController();
  const timeout = options.timeoutMs
    ? setTimeout(() => controller.abort(), options.timeoutMs)
    : null;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!res.ok) {
      throw new Error(`POST ${url} failed: ${res.status}`);
    }

    return res.json();
  } catch (error) {
    datadogRum.addError(error, { url, functionName: "postJson" });
    // Polling to ensure we update online status because some android devices don't trigger online/offline events correctly.
    await networkPoller.startPolling();
    throw error;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
};
